/**
 * The ready message for a customer asking "where is my order?" — the partner's status lookup
 * (his `api/order-status/[orderNumber]`), with what HQ knows on top: an order handed over or
 * completed, a parcel marked received, an orange row. His wording is kept word for word (informal
 * «σου»); the "it's here" message is HQ's arrival DM from the shipment page (formal «σας»). The page
 * shows it in a box to edit before copying.
 */
import type { CustomStatus, DeliveryChoice } from "./medusa"
import { localized } from "./i18n"
import { MESSAGE_BY_KEY, render } from "./message-defaults"
import { arrivalVars } from "./parcels"

// His two tests, in the order his note 13 asks for: "Out for delivery" also contains "deliver", so
// the more specific one goes first.
const OUT = /(out for delivery|προς παράδοση|διανομή)/i
const DELIVERED = /(deliver|παραδό|παραλήφθηκε|delivered|επιδόθηκε)/i

export type MessageKind = "prepared" | "on_the_way" | "out_for_delivery" | "arrived" | "delivered"

export const MESSAGE_LABEL: Record<MessageKind, string> = localized(
  {
    prepared: "Not shipped yet",
    on_the_way: "On the way",
    out_for_delivery: "Out for delivery",
    arrived: "Here — to arrange",
    delivered: "Delivered",
  },
  "message"
)

export type MessageInput = {
  number: string
  status: CustomStatus
  /** The Tracking tab's status for the order's parcel (Euthymis's 17TRACK stages). */
  parcelStatus: string | null
  receivedAt: string | null
  handedOverAt: string | null
  choice: DeliveryChoice | null
  left: number | null
}

/**
 * Today's «no parcel after 20 days» (16 Oct 2026): the custom order is still at the supplier or with
 * Larry. Days since the order date, or null when the order is in a parcel, here, or finished.
 */
export const NO_PARCEL_DAYS = 20
export function daysWithoutParcel(order: { status: CustomStatus; parcel_status: string | null; athens_date: string | null }, today: string): number | null {
  if (order.status !== "active" || (order.parcel_status ?? "").trim() || !order.athens_date) return null
  return Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${order.athens_date.slice(0, 10)}T00:00:00Z`)) / 86_400_000)
}

const defaultTemplate = (key: string) => MESSAGE_BY_KEY.get(key)?.text ?? ""

/** The update for a customer whose order is taking longer (formal «σας», like the arrival message). */
export const delayText = (number: string) => render(defaultTemplate("custom.delay"), { number })

/**
 * Which message a custom order needs now, and its placeholders (lib/message-defaults.ts): the
 * wording comes from the owners' messages (`template`, HQ's own by default).
 */
export function customerMessageParts(input: MessageInput, template: (key: string) => string = defaultTemplate): { kind: MessageKind; key: string; vars: Record<string, string> } | null {
  const number = input.number
  if (input.status === "cancelled") return null
  if (input.handedOverAt || input.status === "completed") return { kind: "delivered", key: "custom.delivered", vars: { number } }
  // An orange row, or a parcel HQ marked received: it is with us, not yet with the customer.
  const arrived = () => ({ kind: "arrived" as const, key: "custom.arrived", vars: arrivalVars(number, input.choice, input.left, template) })
  if (input.receivedAt || input.status === "pending") return arrived()
  const status = input.parcelStatus?.trim() || null
  if (!status) return { kind: "prepared", key: "custom.prepared", vars: { number } }
  if (OUT.test(status)) return { kind: "out_for_delivery", key: "custom.out_for_delivery", vars: { number } }
  // Larry's parcels come to the shop first, so "delivered" on the tracking means it reached US:
  // his "παραδόθηκε!" would tell the customer they have it.
  if (DELIVERED.test(status)) return arrived()
  return { kind: "on_the_way", key: "custom.on_the_way", vars: { number, status } }
}

/** The message in HQ's own wording (the order page uses the owners' through MessageBox). */
export function customerMessage(input: MessageInput): { kind: MessageKind; text: string } | null {
  const parts = customerMessageParts(input)
  return parts ? { kind: parts.kind, text: render(defaultTemplate(parts.key), parts.vars) } : null
}
